import React, { useMemo } from "react";
import { DataGrid, GridToolbarContainer, GridToolbarQuickFilter, GridToolbarExport, GridToolbarDensitySelector, GridToolbarFilterButton, GridToolbarColumnsButton } from "@mui/x-data-grid";
import { Box } from "@mui/material";

const FR_LOCALE = {
  noRowsLabel: "Aucune donnée",
  noResultsOverlayLabel: "Aucun résultat trouvé.",
  toolbarColumns: "Colonnes",
  toolbarColumnsLabel: "Gérer les colonnes",
  toolbarFilters: "Filtres",
  toolbarFiltersLabel: "Afficher les filtres",
  toolbarFiltersTooltipHide: "Masquer les filtres",
  toolbarFiltersTooltipShow: "Afficher les filtres",
  toolbarFiltersTooltipActive: (count) => count > 1 ? `${count} filtres actifs` : `${count} filtre actif`,
  toolbarDensity: "Densité",
  toolbarDensityLabel: "Densité",
  toolbarDensityCompact: "Compact",
  toolbarDensityStandard: "Normal",
  toolbarDensityComfortable: "Confortable",
  toolbarExport: "Exporter",
  toolbarExportLabel: "Exporter",
  toolbarExportCSV: "Télécharger en CSV",
  toolbarExportPrint: "Imprimer",
  columnsPanelTextFieldLabel: "Chercher une colonne",
  columnsPanelTextFieldPlaceholder: "Nom de colonne",
  columnsPanelDragIconLabel: "Réorganiser",
  columnsPanelShowAllButton: "Tout afficher",
  columnsPanelHideAllButton: "Tout masquer",
  filterPanelAddFilter: "Ajouter un filtre",
  filterPanelRemoveAll: "Tout supprimer",
  filterPanelDeleteIconLabel: "Supprimer",
  filterPanelLogicOperator: "Opérateur",
  filterPanelOperator: "Opérateur",
  filterPanelOperatorAnd: "Et",
  filterPanelOperatorOr: "Ou",
  filterPanelColumns: "Colonne",
  filterPanelInputLabel: "Valeur",
  filterPanelInputPlaceholder: "Valeur du filtre",
  filterOperatorContains: "contient",
  filterOperatorDoesNotContain: "ne contient pas",
  filterOperatorEquals: "égal à",
  filterOperatorDoesNotEqual: "différent de",
  filterOperatorStartsWith: "commence par",
  filterOperatorEndsWith: "finit par",
  filterOperatorIs: "est",
  filterOperatorNot: "n'est pas",
  filterOperatorAfter: "après",
  filterOperatorOnOrAfter: "à partir de",
  filterOperatorBefore: "avant",
  filterOperatorOnOrBefore: "jusqu'à",
  filterOperatorIsEmpty: "est vide",
  filterOperatorIsNotEmpty: "n'est pas vide",
  filterOperatorIsAnyOf: "est l'un de",
  columnMenuLabel: "Menu",
  columnMenuShowColumns: "Afficher les colonnes",
  columnMenuManageColumns: "Gérer les colonnes",
  columnMenuFilter: "Filtrer",
  columnMenuHideColumn: "Masquer la colonne",
  columnMenuUnsort: "Annuler le tri",
  columnMenuSortAsc: "Tri croissant",
  columnMenuSortDesc: "Tri décroissant",
  columnHeaderSortIconLabel: "Trier",
  columnHeaderFiltersLabel: "Afficher les filtres",
  columnHeaderFiltersTooltipActive: (count) => count > 1 ? `${count} filtres actifs` : `${count} filtre actif`,
  footerRowSelected: (count) => count > 1 ? `${count} lignes sélectionnées` : `${count} ligne sélectionnée`,
  footerTotalRows: "Total :",
  footerTotalVisibleRows: (visibleCount, totalCount) => `${visibleCount} sur ${totalCount}`,
  checkboxSelectionHeaderName: "Sélection",
  checkboxSelectionSelectAllRows: "Tout sélectionner",
  checkboxSelectionUnselectAllRows: "Tout désélectionner",
  checkboxSelectionSelectRow: "Sélectionner la ligne",
  checkboxSelectionUnselectRow: "Désélectionner la ligne",
  MuiTablePagination: {
    labelRowsPerPage: "Lignes par page :",
    labelDisplayedRows: ({ from, to, count }) => `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`,
  },
};

function CustomToolbar({ showExport = true, showDensity = true, showFilter = true, showColumns = true, extraButtons }) {
  return (
    <GridToolbarContainer sx={{ gap: 0.5, px: 1 }}>
      {showColumns && <GridToolbarColumnsButton />}
      {showFilter && <GridToolbarFilterButton />}
      {showDensity && <GridToolbarDensitySelector />}
      {showExport && <GridToolbarExport />}
      {extraButtons}
      <Box sx={{ flex: 1 }} />
      <GridToolbarQuickFilter debounceMs={300} placeholder="Rechercher..." sx={{ minWidth: 200 }} />
    </GridToolbarContainer>
  );
}

export default function DataGridFr({
  rows = [],
  columns = [],
  loading = false,
  checkboxSelection = false,
  onRowSelectionModelChange,
  rowSelectionModel,
  getRowId,
  initialSortModel,
  pageSize = 25,
  showExport = true,
  showDensity = true,
  showFilter = true,
  showColumns = true,
  extraToolbarButtons,
  onRowClick,
  autoHeight = true,
  density = "compact",
  disableRowSelectionOnClick = true,
  sx,
  ...rest
}) {
  const paginationModel = useMemo(() => ({ pageSize, page: 0 }), [pageSize]);
  const sortModel = useMemo(() => initialSortModel || [], [initialSortModel]);

  const toolbar = useMemo(
    () => () => (
      <CustomToolbar
        showExport={showExport}
        showDensity={showDensity}
        showFilter={showFilter}
        showColumns={showColumns}
        extraButtons={extraToolbarButtons}
      />
    ),
    [showExport, showDensity, showFilter, showColumns, extraToolbarButtons]
  );

  return (
    <DataGrid
      rows={rows}
      columns={columns}
      loading={loading}
      density={density}
      autoHeight={autoHeight}
      checkboxSelection={checkboxSelection}
      disableRowSelectionOnClick={disableRowSelectionOnClick}
      onRowSelectionModelChange={onRowSelectionModelChange}
      rowSelectionModel={rowSelectionModel}
      getRowId={getRowId}
      initialState={{
        sorting: { sortModel },
        pagination: { paginationModel },
      }}
      pageSizeOptions={[10, 25, 50, 100]}
      localeText={FR_LOCALE}
      slots={{ toolbar }}
      slotProps={{ toolbar: { showQuickFilter: true } }}
      onRowClick={onRowClick}
      sx={{
        border: "none",
        "& .MuiDataGrid-cell": { py: 0.5 },
        "& .MuiDataGrid-columnHeaders": { bgcolor: "grey.50" },
        "& .MuiDataGrid-row:hover": { bgcolor: "action.hover" },
        ...sx,
      }}
      {...rest}
    />
  );
}
